import { useState, useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useWireValue } from '@forminator/react-wire'
import * as actions from '@actions'
import * as store from '@store'
import useAbortableFetch from '@/hook/useAbortableFetch'
import { toast } from 'react-toastify'

import CheckRevenueTable from '../CheckRevenueTable'
import CheckProductionTable from '../CheckProductionTable'
import CheckDateRangeFilter from '../CheckDateRangeFilter'

const itemsPerPage = 10

const WellDetail = () => {
    
    const { wellName } = useParams(null)
    const [searchParams] = useSearchParams()
    
    const [checkDetails, setCheckDetails] = useState(null)
    const [checkProductions, setCheckProductions] = useState(null)
    const [checkRevenue, setCheckRevenue] = useState(null)
    const [pageIndex, setPageIndex] = useState(0)
    const [pageIndexProduction, setPageIndexProduction] = useState(0)
    const [dateRangeFilter, setDateRangeFilter] = useState({})
    const currentOrganization = useWireValue(store.currentOrganization)
    const apiNumber = useMemo(() => searchParams.get('apiNumber'), [searchParams])
    
    // whenever changing org i have to reset all variables
    useEffect(() => {
        setDateRangeFilter({})
        setPageIndex(0)
        setCheckDetails(null)
        setCheckProductions(null)
        setCheckRevenue(null)
    }, [currentOrganization])
    
    // whenever filtering date i need to clear page index
    useEffect(() => {
        setPageIndex(0)
        setPageIndexProduction(0)
    }, [dateRangeFilter])
    
    useAbortableFetch(() => {
        
        if (!wellName?.length) return
        
        const key = apiNumber ? 'apiNumber' : 'property'
        const value = apiNumber ? apiNumber : wellName
        
        actions.fetchCheckDetailsRevenue(key, value, dateRangeFilter)
            .then(it => setCheckRevenue(it))
            .catch(e => {
                console.error(e)
                toast.error('Failed to fetch well revenue')
            })
        
    }, [wellName, currentOrganization, dateRangeFilter, apiNumber])
    
    useAbortableFetch(() => {
        
        if (!wellName?.length) return
        
        const key = apiNumber ? 'apiNumber' : 'property'
        const value = apiNumber ? apiNumber : wellName
        
        actions.fetchCheckDetailsProduction(key, value, {
            offset: (pageIndexProduction * itemsPerPage),
            limit: itemsPerPage,
            ...dateRangeFilter,
        })
            .then(it => setCheckProductions(it))
            .catch(e => {
                console.error(e)
                toast.error('Failed to fetch well production')
            })
        
    }, [wellName, pageIndexProduction, currentOrganization, dateRangeFilter])
    
    useAbortableFetch(() => {
        
        if (!wellName?.length) return
        
        const key = apiNumber ? 'apiNumber' : 'property'
        const value = apiNumber ? apiNumber : wellName
        
        actions.fetchCheckDetailsMonthly(key, value, {
            offset: (pageIndex * itemsPerPage),
            limit: itemsPerPage,
            ...dateRangeFilter,
        })
            .then(it => setCheckDetails(it))
            .catch(e => {
                console.error(e)
                toast.error('Failed to fetch well details')
            })
        
    }, [wellName, pageIndex, currentOrganization, dateRangeFilter])
    
    return (
        
        <div className="WellDetail">
            
            <CheckDateRangeFilter setDateRangeFilter={setDateRangeFilter} resetOn={[currentOrganization]} />
            
            <div className="summary mt-4">
                <h3 className="text-xl">{wellName}</h3>
            </div>
            
            <h4 className="mt-4 text-xl">Revenue</h4>
            <CheckRevenueTable
                className="mt-4"
                rows={checkDetails?.rows}
                checkRevenue={checkRevenue}
                itemsPerPage={itemsPerPage}
                pageCount={checkDetails?.count || 0}
                setPageIndex={setPageIndex} />
            
            <h4 className="text-xl">Production</h4>
            <CheckProductionTable
                className="mt-4"
                rows={checkProductions?.rows}
                itemsPerPage={itemsPerPage}
                pageCount={checkProductions?.count || 0}
                setPageIndex={setPageIndexProduction} />
            
            {/* <div><pre><code className="text-white">
                {JSON.stringify(well, null, 4)}
            </code></pre></div> */}
        
        </div>
        
    )
    
}

export default WellDetail
